import { Maximize2, Minimize2 } from 'lucide-react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { humanizeReportToolName } from '@apps/web/components/phoebe_chat_view/tool_calls/infrastructure_tool_ui';
import {
    buildCsv,
    buildPdfBlob,
    buildTsv,
    buildXlsxBlob,
    CSV_BOM,
    reportFilename,
    triggerBlobDownload,
} from '@apps/web/lib/tabular_export';
import { cn } from '@apps/web/lib/utils';

// Structured-report rendering is shape-driven, not tool-name-driven: any
// tool whose output is `{columns: string[], rows: string[][], summary?:
// {...}}` is rendered here (see parseStructuredReportFromOutput). Adding a
// new report tool needs NO change in this file — the generic
// `generate_report` tool already covers the general case. A literal tool
// name is only referenced for cosmetic labels (humanizeReportToolName).
// Prop shape for the pill component that renders a structured report
// as an attachment below the assistant text. The pill toggles the
// full `StructuredReportTable` inline below itself when clicked.
export type ReportArtifact = {
    readonly id: string;
    readonly toolName: string;
    readonly title: string;
    readonly columns: ReadonlyArray<string>;
    readonly rows: ReadonlyArray<ReadonlyArray<string>>;
    readonly summary: {
        readonly returned: number;
        readonly truncated: boolean;
        readonly filters: Record<string, unknown>;
    };
};

// Auto-dismiss a failed-export message after this long so a stale error
// doesn't linger beside the buttons.
const EXPORT_ERROR_DISMISS_MS = 5000;

function StructuredReportTable({
    columns,
    rows,
    summary,
    filenameSlug,
    title,
}: {
    readonly columns: ReadonlyArray<string>;
    readonly rows: ReadonlyArray<ReadonlyArray<string>>;
    readonly summary: {
        readonly returned: number;
        readonly truncated: boolean;
        readonly filters: Record<string, unknown>;
    };
    // Snake-case slug used in the export filename. Derived from the
    // report's tool name so a future report tool exports under its
    // own name instead of all reports landing as `outreach_report_*`.
    readonly filenameSlug: string;
    // Human-readable report title, used as the heading inside the PDF.
    readonly title: string;
}) {
    const [copied, setCopied] = useState<boolean>(false);
    const [copyError, setCopyError] = useState<string | null>(null);
    // Which binary export is generating, so its button shows progress and
    // both async buttons disable until it settles.
    const [busyFormat, setBusyFormat] = useState<'xlsx' | 'pdf' | null>(null);
    const [exportError, setExportError] = useState<string | null>(null);
    // Track timers so an unmount mid-toast / mid-download doesn't fire
    // state updates on an unmounted component or race the blob revoke.
    const copiedTimerRef = useRef<number | null>(null);
    const exportErrorTimerRef = useRef<number | null>(null);
    // Guards async export state updates from landing after unmount.
    const mountedRef = useRef<boolean>(true);
    useEffect(() => {
        // Reset on mount: StrictMode/fast-refresh runs the cleanup then
        // re-runs setup on the same ref, so without this the guard would
        // stay false after a remount and silently no-op every export.
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (copiedTimerRef.current !== null) {
                window.clearTimeout(copiedTimerRef.current);
            }
            if (exportErrorTimerRef.current !== null) {
                window.clearTimeout(exportErrorTimerRef.current);
            }
        };
    }, []);

    // Clear a shown export error (and its pending auto-dismiss) when the
    // user takes any other export action, so a stale failure message
    // doesn't linger after an unrelated success.
    function clearExportError() {
        if (exportErrorTimerRef.current !== null) {
            window.clearTimeout(exportErrorTimerRef.current);
            exportErrorTimerRef.current = null;
        }
        setExportError(null);
    }

    function handleCopy() {
        clearExportError();
        const tsv = buildTsv(columns, rows);
        if (
            typeof navigator === 'undefined' ||
            !navigator.clipboard?.writeText
        ) {
            setCopyError('Clipboard unavailable');
            return;
        }
        navigator.clipboard
            .writeText(tsv)
            .then(() => {
                setCopied(true);
                setCopyError(null);
                if (copiedTimerRef.current !== null) {
                    window.clearTimeout(copiedTimerRef.current);
                }
                copiedTimerRef.current = window.setTimeout(() => {
                    setCopied(false);
                    copiedTimerRef.current = null;
                }, 1500);
            })
            .catch(() => {
                setCopyError('Copy failed');
            });
    }

    function handleDownloadCsv() {
        clearExportError();
        // UTF-8 BOM forces Excel/Numbers to detect encoding for non-ASCII.
        const blob = new Blob([CSV_BOM + buildCsv(columns, rows)], {
            type: 'text/csv;charset=utf-8',
        });
        triggerBlobDownload(blob, reportFilename(filenameSlug, 'csv'));
    }

    // Excel (.xlsx) and PDF are generated from lazy-loaded libraries, so
    // the handler is async and shows per-format progress / error.
    async function runBinaryExport(format: 'xlsx' | 'pdf') {
        clearExportError();
        setBusyFormat(format);
        try {
            const blob =
                format === 'xlsx'
                    ? await buildXlsxBlob(columns, rows)
                    : await buildPdfBlob(columns, rows, title);
            // Always deliver the file, even if the user switched tabs or
            // closed the panel mid-generation (the `key` remount unmounts
            // this instance). The download is a document-level side effect,
            // not component state — gating it on mount would silently drop a
            // ready file. Only the setState calls below need the guard.
            triggerBlobDownload(blob, reportFilename(filenameSlug, format));
        } catch {
            if (mountedRef.current) {
                setExportError(
                    format === 'xlsx'
                        ? 'Excel export failed'
                        : 'PDF export failed',
                );
                exportErrorTimerRef.current = window.setTimeout(() => {
                    setExportError(null);
                    exportErrorTimerRef.current = null;
                }, EXPORT_ERROR_DISMISS_MS);
            }
        } finally {
            if (mountedRef.current) {
                setBusyFormat(null);
            }
        }
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col space-y-2">
            <div className="text-muted-foreground flex items-center justify-between text-xs">
                <span>
                    {summary.returned} row{summary.returned === 1 ? '' : 's'}
                    {summary.truncated ? ' (truncated)' : ''}
                </span>
                <div className="flex flex-wrap items-center gap-1.5">
                    <button
                        type="button"
                        onClick={handleCopy}
                        className="hover:bg-muted rounded-md border px-2 py-1 text-xs"
                    >
                        {copyError !== null
                            ? copyError
                            : copied
                              ? 'Copied'
                              : 'Copy as TSV'}
                    </button>
                    <button
                        type="button"
                        onClick={handleDownloadCsv}
                        aria-label="Download CSV"
                        className="hover:bg-muted rounded-md border px-2 py-1 text-xs"
                    >
                        CSV
                    </button>
                    <button
                        type="button"
                        onClick={() => void runBinaryExport('xlsx')}
                        disabled={busyFormat !== null}
                        aria-label="Download as Excel"
                        aria-busy={busyFormat === 'xlsx'}
                        className="hover:bg-muted rounded-md border px-2 py-1 text-xs disabled:pointer-events-none disabled:opacity-50"
                    >
                        {busyFormat === 'xlsx' ? 'Excel…' : 'Excel'}
                    </button>
                    <button
                        type="button"
                        onClick={() => void runBinaryExport('pdf')}
                        disabled={busyFormat !== null}
                        aria-label="Download as PDF"
                        aria-busy={busyFormat === 'pdf'}
                        className="hover:bg-muted rounded-md border px-2 py-1 text-xs disabled:pointer-events-none disabled:opacity-50"
                    >
                        {busyFormat === 'pdf' ? 'PDF…' : 'PDF'}
                    </button>
                    {exportError !== null && (
                        <span className="text-destructive text-xs">
                            {exportError}
                        </span>
                    )}
                </div>
            </div>
            <div className="min-h-0 shrink overflow-auto rounded-md border">
                <table className="text-xs">
                    <thead className="bg-muted sticky top-0 z-10">
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col}
                                    className="px-2 py-1 text-left font-medium whitespace-nowrap"
                                >
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr
                                key={rowIndex}
                                className="border-border/40 border-t"
                            >
                                {row.map((cell, cellIndex) => (
                                    <td
                                        key={cellIndex}
                                        title={cell}
                                        className="max-w-[240px] px-2 py-1 align-top break-words"
                                    >
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// Attachment-style entry for a structured report. The pill sits in
// chat (below the assistant text); clicking it toggles the full
// `StructuredReportTable` inline beneath the pill. All rendering
// stays inside the chat DOM tree — no portals/overlays/dialogs that
// escape the Phoebe layout context (mode, theme, etc.).
// Module-level store: artifacts opened across the chat session and
// which one is active. One source of truth so opening a second pill
// activates its tab in the shared pane rather than stacking another
// panel on top.
type ArtifactStoreState = {
    readonly open: ReadonlyArray<ReportArtifact>;
    readonly activeId: string | null;
    // When true, render the panel as a centered modal even if the
    // chat is in full-screen split mode. Lets the admin pop the
    // active artifact out for a larger view.
    readonly forceModal: boolean;
};

let artifactStoreState: ArtifactStoreState = {
    open: [],
    activeId: null,
    forceModal: false,
};
const artifactStoreListeners = new Set<() => void>();

function emitArtifactStoreChange(next: ArtifactStoreState): void {
    artifactStoreState = next;
    for (const listener of artifactStoreListeners) {
        listener();
    }
}

// Dedupe key — keyed on the filter shape AND the requested columns
// that define "what report this is". Same filters + same columns
// re-run replaces the tab in place; any change (different time
// window, different status, or different column set) opens a new
// tab so the admin can compare two views side-by-side. `limit` and
// `default_window_applied` stay out of the key — they're truncation
// metadata, not report identity.
const _TAB_KEY_FILTER_FIELDS = [
    'organization_id',
    'outreach_type',
    'status',
    'time_range',
    'date_from',
    'date_to',
] as const;

function artifactTabKey(a: ReportArtifact): string {
    const filters = a.summary.filters;
    const parts: Array<string> = [a.toolName];
    for (const field of _TAB_KEY_FILTER_FIELDS) {
        parts.push(String(filters[field] ?? ''));
    }
    parts.push(a.columns.join(','));
    return parts.join('|');
}

// Short tab label — always leads with the report kind ("Outreach",
// "Callouts", "Shift confirmations") so a row of tabs that differ
// only on time range still tells the admin what each one is. Time
// segments are compacted ("last 7 days" → "7d") to keep the face
// short. The full long-form label lives on the panel header.
function artifactTabLabel(a: ReportArtifact): string {
    const filters = a.summary.filters;
    const segments: Array<string> = [artifactKindLabel(a)];
    const status = filters['status'];
    if (typeof status === 'string' && status.length > 0) {
        segments.push(status.split('_').join(' '));
    }
    const time = compactTimeRange(filters);
    if (time) {
        segments.push(time);
    }
    if (filters['organization_id']) {
        segments.push('org');
    }
    return segments.join(' · ');
}

function artifactKindLabel(a: ReportArtifact): string {
    const type = a.summary.filters['outreach_type'];
    if (typeof type === 'string' && type.length > 0) {
        return humanizeOutreachType(type);
    }
    if (a.toolName === 'query_outreach_report') {
        return 'Outreach';
    }
    return humanizeReportToolName(a.toolName);
}

function compactTimeRange(
    filters: Readonly<Record<string, unknown>>,
): string | null {
    const range = filters['time_range'];
    if (typeof range === 'string' && range.length > 0) {
        const match = range.match(/(\d+)\s*(day|week|month|hour|year)s?/i);
        if (match) {
            return `${match[1]}${match[2]!.charAt(0).toLowerCase()}`;
        }
        return range;
    }
    const from = filters['date_from'];
    const to = filters['date_to'];
    if (typeof from === 'string' && typeof to === 'string') {
        return `${from.slice(5, 10)}→${to.slice(5, 10)}`;
    }
    if (typeof from === 'string') {
        return `from ${from.slice(5, 10)}`;
    }
    if (typeof to === 'string') {
        return `to ${to.slice(5, 10)}`;
    }
    return null;
}

// Longer header label — includes the report kind prefix so the active
// panel reads clearly even when a single tab is open.
function artifactHeaderLabel(a: ReportArtifact): string {
    const segments = artifactDescriptorSegments(a);
    if (segments.length === 0) {
        return humanizeReportToolName(a.toolName);
    }
    return `${humanizeReportToolName(a.toolName)} · ${segments.join(' · ')}`;
}

function artifactDescriptorSegments(a: ReportArtifact): Array<string> {
    const filters = a.summary.filters;
    const segments: Array<string> = [];
    const type = filters['outreach_type'];
    if (typeof type === 'string' && type.length > 0) {
        segments.push(humanizeOutreachType(type));
    }
    const status = filters['status'];
    if (typeof status === 'string' && status.length > 0) {
        segments.push(status.split('_').join(' '));
    }
    const timeRange = filters['time_range'];
    if (typeof timeRange === 'string' && timeRange.length > 0) {
        segments.push(timeRange);
    } else {
        const dateFrom = filters['date_from'];
        const dateTo = filters['date_to'];
        if (typeof dateFrom === 'string' && typeof dateTo === 'string') {
            segments.push(`${dateFrom.slice(0, 10)} → ${dateTo.slice(0, 10)}`);
        } else if (typeof dateFrom === 'string') {
            segments.push(`from ${dateFrom.slice(0, 10)}`);
        } else if (typeof dateTo === 'string') {
            segments.push(`to ${dateTo.slice(0, 10)}`);
        }
    }
    if (filters['organization_id']) {
        segments.push('org-scoped');
    }
    return segments;
}

function humanizeOutreachType(type: string): string {
    switch (type) {
        case 'callout':
            return 'Callouts';
        case 'shift_confirmation':
            return 'Shift confirmations';
        case 'shift_reminder_clock_in':
            return 'Clock-in reminders';
        case 'shift_reminder_clock_out':
            return 'Clock-out reminders';
        default:
            return type
                .split('_')
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');
    }
}

function openArtifactTab(artifact: ReportArtifact): void {
    const key = artifactTabKey(artifact);
    const existed = artifactStoreState.open.some(
        (a) => artifactTabKey(a) === key,
    );
    emitArtifactStoreChange({
        open: existed
            ? artifactStoreState.open.map((a) =>
                  artifactTabKey(a) === key ? artifact : a,
              )
            : [...artifactStoreState.open, artifact],
        activeId: artifact.id,
        forceModal: false,
    });
}

function closeArtifactTab(id: string): void {
    const next = artifactStoreState.open.filter((a) => a.id !== id);
    let activeId: string | null = artifactStoreState.activeId;
    if (activeId === id) {
        activeId = next.length > 0 ? (next[next.length - 1]?.id ?? null) : null;
    }
    emitArtifactStoreChange({
        open: next,
        activeId,
        forceModal: activeId === null ? false : artifactStoreState.forceModal,
    });
}

function setActiveArtifactTab(id: string | null): void {
    // Preserve the current view mode (split vs expanded modal) so
    // clicking a tab swaps the content without bouncing the admin
    // back to side-pane.
    emitArtifactStoreChange({ ...artifactStoreState, activeId: id });
}

function setArtifactForceModal(value: boolean): void {
    emitArtifactStoreChange({ ...artifactStoreState, forceModal: value });
}

export function closeAllArtifactTabs(): void {
    emitArtifactStoreChange({ open: [], activeId: null, forceModal: false });
}

function useArtifactStore(): ArtifactStoreState {
    return useSyncExternalStore(
        (onChange) => {
            artifactStoreListeners.add(onChange);
            return () => {
                artifactStoreListeners.delete(onChange);
            };
        },
        () => artifactStoreState,
        () => artifactStoreState,
    );
}

// Public hook for layout consumers: does an artifact panel currently
// occupy the right-side split slot? Returns true only when an
// artifact is open AND the user has not popped it out to the centered
// modal — in modal mode the chat overlay doesn't need to reserve
// gutter for the panel.
export function useHasOpenStructuredReport(): boolean {
    const { activeId, forceModal } = useArtifactStore();
    return activeId !== null && !forceModal;
}

// Drag-resizable width for the right-edge structured-report panel.
// Stored as a module-level pixel value so both the panel and the
// chat-overlay gutter in app_layout read the same source of truth.
// Defaults to 50% of viewport on first mount, clamps to a minimum
// 480px and a maximum that leaves the chat at least 360px on the
// left. Persists across reloads via localStorage.
// Just enough floor on each side that neither pane disappears
// entirely — the user gets full freedom otherwise.
const _PANEL_WIDTH_MIN_PX = 200;
const _PANEL_WIDTH_LEFT_GUTTER_PX = 200;
const _PANEL_WIDTH_STORAGE_KEY = 'phoebe-structured-report-panel-width';

function clampPanelWidth(px: number): number {
    if (typeof window === 'undefined') {
        return px;
    }
    const max = Math.max(
        _PANEL_WIDTH_MIN_PX,
        window.innerWidth - _PANEL_WIDTH_LEFT_GUTTER_PX,
    );
    return Math.max(_PANEL_WIDTH_MIN_PX, Math.min(px, max));
}

function readStoredPanelWidth(): number {
    if (typeof window === 'undefined') {
        return 640;
    }
    try {
        const raw = window.localStorage.getItem(_PANEL_WIDTH_STORAGE_KEY);
        if (raw !== null) {
            const parsed = Number.parseInt(raw, 10);
            if (Number.isFinite(parsed)) {
                return clampPanelWidth(parsed);
            }
        }
    } catch {
        // localStorage may throw in private-browsing modes; fall
        // through to the viewport default.
    }
    return clampPanelWidth(Math.round(window.innerWidth * 0.5));
}

let artifactPanelWidthPx: number = (() => {
    if (typeof window === 'undefined') {
        return 640;
    }
    return readStoredPanelWidth();
})();
const artifactPanelWidthListeners = new Set<() => void>();

function setArtifactPanelWidth(px: number): void {
    const next = clampPanelWidth(px);
    if (next === artifactPanelWidthPx) {
        return;
    }
    artifactPanelWidthPx = next;
    try {
        window.localStorage.setItem(_PANEL_WIDTH_STORAGE_KEY, String(next));
    } catch {
        // ignore quota / private-mode failures
    }
    for (const listener of artifactPanelWidthListeners) {
        listener();
    }
}

export function useStructuredReportPanelWidth(): number {
    return useSyncExternalStore(
        (onChange) => {
            artifactPanelWidthListeners.add(onChange);
            return () => {
                artifactPanelWidthListeners.delete(onChange);
            };
        },
        () => artifactPanelWidthPx,
        () => 640,
    );
}

export function StructuredReportArtifactPill({
    artifact,
}: {
    readonly artifact: ReportArtifact;
}) {
    const { activeId } = useArtifactStore();
    const isActive = activeId === artifact.id;
    return (
        <button
            type="button"
            onClick={() => openArtifactTab(artifact)}
            aria-pressed={isActive}
            className={cn(
                'hover:bg-muted flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-xs transition-colors',
                isActive && 'border-foreground/40 bg-muted',
            )}
        >
            <span className="flex flex-col gap-0.5">
                <span className="text-foreground font-medium">
                    {artifact.title}
                </span>
                <span className="text-muted-foreground">
                    {artifact.summary.returned} row
                    {artifact.summary.returned === 1 ? '' : 's'}
                    {artifact.summary.truncated ? ' (truncated)' : ''}
                    {' · '}
                    {artifact.columns.length} column
                    {artifact.columns.length === 1 ? '' : 's'}
                </span>
            </span>
            <span className="text-muted-foreground text-xs">
                {isActive ? 'Open' : 'Open ›'}
            </span>
        </button>
    );
}

// Shared Devin-style tabbed panel. Rendered once at PhoebeChatView
// scope so it survives across pill clicks and shows tabs for every
// opened report. In sidebar (non-expanded) chat mode it's a centered
// modal with backdrop. In full-screen mode it's a right-edge slide-in
// pane beside the chat, with tabs across the top.
// Thin grab strip on the panel's left edge. Pointer-drag moves the
// edge horizontally — moving left widens the panel, right shrinks
// it. Width persists via the module-level store so the layout's
// chat-overlay gutter reads the same value.
function PanelResizeHandle() {
    const panelWidthPx = useStructuredReportPanelWidth();
    const dragRef = useRef<{
        readonly startX: number;
        readonly startWidth: number;
    } | null>(null);

    useEffect(() => {
        function onPointerMove(event: PointerEvent) {
            if (!dragRef.current) {
                return;
            }
            const delta = dragRef.current.startX - event.clientX;
            setArtifactPanelWidth(dragRef.current.startWidth + delta);
        }
        function onPointerUp() {
            // Only clear body styles when this handle was the active
            // drag — pointerup also fires for unrelated clicks (other
            // resize handles, buttons) and clearing globally would
            // stomp on their drag-in-progress styles.
            if (!dragRef.current) {
                return;
            }
            dragRef.current = null;
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        }
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerUp);
            // If the panel unmounts mid-drag (e.g. Escape closes the
            // active tab while the user is holding the handle),
            // pointerup never fires — restore body styles here so
            // the page doesn't get stuck with a col-resize cursor
            // and disabled text selection.
            if (dragRef.current) {
                dragRef.current = null;
                document.body.style.userSelect = '';
                document.body.style.cursor = '';
            }
        };
    }, []);

    return (
        <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize report panel"
            onPointerDown={(event) => {
                dragRef.current = {
                    startX: event.clientX,
                    startWidth: panelWidthPx,
                };
                document.body.style.userSelect = 'none';
                document.body.style.cursor = 'col-resize';
                event.preventDefault();
            }}
            className="hover:bg-foreground/20 active:bg-foreground/30 absolute top-0 bottom-0 left-0 z-10 w-1 cursor-col-resize"
        />
    );
}

export function StructuredReportArtifactsPanel({
    splitMode,
}: {
    readonly splitMode: boolean;
}) {
    const { open, activeId, forceModal } = useArtifactStore();
    const panelWidthPx = useStructuredReportPanelWidth();
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);
    const active = open.find((a) => a.id === activeId) ?? null;
    const effectiveSplitMode = splitMode && !forceModal;

    useEffect(() => {
        if (!active) {
            return;
        }
        const handler = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (forceModal) {
                    setArtifactForceModal(false);
                    return;
                }
                closeArtifactTab(active.id);
            }
        };
        window.addEventListener('keydown', handler);
        closeButtonRef.current?.focus();
        if (effectiveSplitMode) {
            return () => {
                window.removeEventListener('keydown', handler);
            };
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', handler);
            document.body.style.overflow = previousOverflow;
        };
    }, [active, effectiveSplitMode, forceModal]);

    if (!active) {
        return null;
    }
    const titleId = `${active.id}-title`;

    const tabBar = (
        // Chrome-style: tabs share the row equally, shrink to a 80px
        // minimum before overflow-x-auto kicks in. Long labels truncate
        // inside each tab; the full label shows in the title tooltip.
        <div className="border-border/60 flex shrink-0 items-stretch gap-0.5 overflow-x-auto border-b px-2 pt-2">
            {open.map((a) => {
                const isThisActive = a.id === active.id;
                return (
                    <div
                        key={a.id}
                        className={cn(
                            // Chrome's tab-sizing model: each tab
                            // grows toward a preferred ~240px width
                            // when there's room and shrinks equally
                            // toward an 80px floor as more tabs open.
                            // overflow-x-auto on the parent kicks in
                            // once tabs hit the floor.
                            'flex max-w-[240px] min-w-[80px] flex-1 items-center gap-1 rounded-t-md border border-b-0 px-2 py-1.5 text-xs',
                            isThisActive
                                ? 'bg-background border-border'
                                : 'bg-muted/40 hover:bg-muted border-transparent',
                        )}
                    >
                        <button
                            type="button"
                            onClick={() => setActiveArtifactTab(a.id)}
                            className={cn(
                                'min-w-0 flex-1 truncate text-left',
                                isThisActive
                                    ? 'text-foreground font-medium'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                            title={artifactTabLabel(a)}
                        >
                            {artifactTabLabel(a)}
                        </button>
                        <button
                            type="button"
                            onClick={() => closeArtifactTab(a.id)}
                            aria-label={`Close ${artifactTabLabel(a)}`}
                            className="text-muted-foreground hover:text-foreground shrink-0 text-xs leading-none"
                        >
                            ×
                        </button>
                    </div>
                );
            })}
        </div>
    );

    const header = (
        <div className="border-border/60 flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3">
            <div className="flex min-w-0 flex-col gap-0.5">
                <p
                    id={titleId}
                    className="text-foreground truncate text-sm font-medium"
                >
                    {artifactHeaderLabel(active)}
                </p>
                <p className="text-muted-foreground text-xs">
                    {active.summary.returned} row
                    {active.summary.returned === 1 ? '' : 's'}
                    {active.summary.truncated
                        ? ' (truncated — narrow filters)'
                        : ''}
                    {' · '}
                    {active.columns.length} column
                    {active.columns.length === 1 ? '' : 's'}
                </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
                {splitMode && (
                    <button
                        type="button"
                        onClick={() => setArtifactForceModal(!forceModal)}
                        aria-label={
                            forceModal
                                ? 'Collapse to side panel'
                                : 'Expand to centered view'
                        }
                        title={
                            forceModal
                                ? 'Collapse to side panel'
                                : 'Expand to centered view'
                        }
                        className="hover:bg-muted rounded-md border px-2 py-1 text-xs"
                    >
                        {forceModal ? (
                            <Minimize2 className="h-3.5 w-3.5" />
                        ) : (
                            <Maximize2 className="h-3.5 w-3.5" />
                        )}
                    </button>
                )}
                <button
                    ref={closeButtonRef}
                    type="button"
                    onClick={closeAllArtifactTabs}
                    aria-label={
                        open.length > 1
                            ? `Close all ${open.length} reports`
                            : 'Close report'
                    }
                    title={
                        open.length > 1
                            ? `Close all ${open.length} reports`
                            : 'Close report'
                    }
                    className="hover:bg-muted rounded-md border px-2 py-1 text-xs"
                >
                    {open.length > 1 ? 'Close all' : 'Close'}
                </button>
            </div>
        </div>
    );

    const body = (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
            <StructuredReportTable
                key={active.id}
                columns={active.columns}
                rows={active.rows}
                summary={active.summary}
                filenameSlug={active.toolName.replace(/^query_/, '')}
                title={artifactHeaderLabel(active)}
            />
        </div>
    );

    return effectiveSplitMode ? (
        <div
            role="dialog"
            aria-modal={false}
            aria-labelledby={titleId}
            className="bg-background fixed top-0 right-0 bottom-0 z-[105] flex flex-col border-l shadow-2xl"
            style={{ width: panelWidthPx }}
        >
            <PanelResizeHandle />
            {tabBar}
            {header}
            {body}
        </div>
    ) : (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40"
            onClick={(event) => {
                if (event.target === event.currentTarget) {
                    closeArtifactTab(active.id);
                }
            }}
        >
            <div className="bg-background flex max-h-[85vh] w-[90vw] max-w-[1100px] flex-col rounded-lg border shadow-xl">
                {tabBar}
                {header}
                {body}
            </div>
        </div>
    );
}
